// Third-party
import React, { Component } from 'react';
import * as Yup from 'yup';
import { withFormik, FormikProps, Form } from 'formik';

// Components
import { FormInput } from '../../../components/forms';
import { PrimaryButton } from '../../../components/buttons';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import notification from '../../../../utils/notification';
import { PERMISSIONS, userHasPermission } from '../../../../utils/permissions';

// Types
import EnterpriseAdminGroup from '@/types/enterprise-admin-group';
import EnterpriseGroupInfos from '@/types/enterprise-group-infos';
import EnterpriseGroup from '@/types/enterprise-group';
import EnterpriseInfos from '@/types/enterprise-infos';
import User from '@/types/user';

// TODO: @deprecate when we deprecate Legacy
import LegacyEnterpriseGroupSettings from './LegacyEnterpriseGroupSettings';
import useThemeVersion from '@/hooks/useThemeVersion';

interface ParentProps {
  group: EnterpriseAdminGroup;
  user: User;
  onSave: (infos: EnterpriseGroupInfos) => Promise<EnterpriseGroup>;
}

type Props = ParentProps & FormikProps<EnterpriseInfos>;

class EnterpriseGroupSettings extends Component<Props> {
  canSubmit = () => {
    const { user } = this.props;

    const { values, initialValues, isSubmitting, errors } = this.props;
    const isDirty = values !== initialValues;
    const hasErrors = Object.keys(errors).length > 0;
    const hasPermission = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS);
    return hasPermission && !hasErrors && isDirty && !isSubmitting;
  };

  render() {
    const { user } = this.props;
    return (
      <div className="enterprise-group-settings">
        <Form className="group-settings-form">
          <FormInput
            className="form-input-v2"
            disabled={!userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS)}
            label={formatMessage('Admin.Enterprise.Fields.GroupName')}
            name="name"
            required
          />

          <PrimaryButton className="save-button primary-v2" disabled={!this.canSubmit()} type="submit">
            <FormattedMessage id="Common.Save" />
          </PrimaryButton>
        </Form>
      </div>
    );
  }
}

const EnterpriseGroupSettingsComponent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <EnterpriseGroupSettings {...props} />;
  }
  return <LegacyEnterpriseGroupSettings {...props} />;
};

const EnterpriseGroupSchema = Yup.object().shape({
  name: Yup.string().required(),
  _id: Yup.string().required(),
});

const formikWrapper = withFormik<Props, EnterpriseGroupInfos>({
  validationSchema: EnterpriseGroupSchema,
  mapPropsToValues: ({ group }) => ({ ...group }),
  handleSubmit: (values, { props, setSubmitting, resetForm }) => {
    props
      .onSave(values)
      .then(() => {
        notification.success(formatMessage('Admin.Enterprise.SaveInfoSuccess'));
        resetForm(values);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(EnterpriseGroupSettingsComponent) as any;
